import { z } from 'zod';

/**
 * License blob shape — duplicated from the runtime's License Zod schema
 * rather than imported, because the portal is meant to ship as its own
 * deployable artifact (different domain, different bundle) and shouldn't
 * pull in the runtime package as a build dep. The shape is small and
 * stable; if the runtime ever bumps a major version the portal updates.
 */
export const PortalLicense = z.object({
  tenantId: z.string(),
  tier: z.enum(['core', 'pro', 'enterprise']),
  features: z.array(z.string()),
  expiresAt: z.number().int().nonnegative().optional(),
  signature: z.string().length(128),
});
export type PortalLicense = z.infer<typeof PortalLicense>;

export type FetchResult =
  | { kind: 'ok'; license: PortalLicense; rawBlob: string }
  | { kind: 'not-found' }
  | { kind: 'revoked' }
  | { kind: 'malformed'; message: string }
  | { kind: 'network'; message: string };

/**
 * Resolve the licensing-server base URL. A production build MUST have
 * `VITE_LICENSING_API_URL` set (Cloudflare Pages env) — falling back to
 * localhost in a deployed bundle silently breaks every request, so we fail
 * loud at load instead. Dev builds keep the localhost default.
 */
function resolveApiBase(): string {
  const fromEnv = import.meta.env['VITE_LICENSING_API_URL'] as string | undefined;
  if (fromEnv !== undefined && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_LICENSING_API_URL is required for production builds — refusing to fall back to ' +
        'http://localhost:8787. Set it in the Cloudflare Pages project env before building.',
    );
  }
  return 'http://localhost:8787';
}

const API_BASE: string = resolveApiBase();

/**
 * Fetch a signed license blob by ID and parse its top-level shape. The
 * portal does NOT verify the Ed25519 signature — that's the runtime's
 * job, on-device. Verifying here adds bundle weight (the public key,
 * `@noble/ed25519`, canonical-JSON) for no real defense: a tampered blob
 * displayed in the portal still fails verification at deploy time, which
 * is the moment that matters.
 */
export async function fetchLicense(licenseId: string): Promise<FetchResult> {
  const id = licenseId.trim();
  if (id.length === 0) return { kind: 'malformed', message: 'License ID required' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/licenses/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : String(err),
    };
  }

  if (res.status === 404) return { kind: 'not-found' };
  if (res.status === 410) return { kind: 'revoked' };
  if (!res.ok) {
    return { kind: 'network', message: `HTTP ${String(res.status)}` };
  }

  const rawBlob = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBlob);
  } catch (err) {
    return {
      kind: 'malformed',
      message: err instanceof Error ? err.message : 'Could not parse JSON',
    };
  }

  const result = PortalLicense.safeParse(parsed);
  if (!result.success) {
    return { kind: 'malformed', message: 'Response did not match License shape' };
  }
  return { kind: 'ok', license: result.data, rawBlob };
}

export const apiBaseUrl = API_BASE;

/**
 * Top-level navigation target that begins the "Sign in with Google" flow.
 * The licensing-server runs the whole OAuth round-trip server-side and
 * redirects the browser back to the Portal with `?sso=google` (success,
 * optionally `&mfa=1` when 2FA is pending) or `?autherror=<code>`. This is a
 * full-page navigation, NOT a fetch — there's no CORS / credentials dance.
 */
export const googleSignInUrl = `${API_BASE}/auth/google/start`;

/* ───── Signup ───── */

export type SignupResult =
  | {
      kind: 'ok';
      tenantId: string;
      launcherUrl: string;
      isNew: boolean;
      editToken: string;
    }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

export async function signup(input: {
  email: string;
  companyName: string;
  /** Cloudflare Turnstile token (when the widget is configured). */
  turnstileToken?: string | null;
}): Promise<SignupResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    });
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'network error',
    };
  }

  if (res.status === 400) {
    const body = (await safeJson(res)) as { issues?: Array<{ message?: string }> };
    return {
      kind: 'invalid',
      message: body.issues?.[0]?.message ?? 'invalid email or company name',
    };
  }
  if (res.status === 403) {
    return { kind: 'invalid', message: 'Verification failed — refresh and try again.' };
  }
  if (res.status === 200 || res.status === 201) {
    const body = (await safeJson(res)) as {
      tenantId?: string;
      launcherUrl?: string;
      isNew?: boolean;
    };
    const editToken = (body as { editToken?: unknown }).editToken;
    if (
      typeof body.tenantId === 'string' &&
      typeof body.launcherUrl === 'string' &&
      typeof body.isNew === 'boolean' &&
      typeof editToken === 'string'
    ) {
      return {
        kind: 'ok',
        tenantId: body.tenantId,
        launcherUrl: body.launcherUrl,
        isNew: body.isNew,
        editToken,
      };
    }
    return {
      kind: 'unexpected',
      status: res.status,
      message: 'server returned success but malformed body',
    };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/**
 * Authorization header for a tenant call. Humans no longer carry an editToken —
 * the session cookie authorizes every write (role-gated server-side), and every
 * call sends credentials:'include'. The token survives only as an optional
 * MACHINE credential (scripts/API), so: bearer when one is explicitly passed,
 * nothing otherwise — never "Bearer null".
 */
export function bearerHeader(editToken: string | null | undefined): Record<string, string> {
  return editToken ? { authorization: `Bearer ${editToken}` } : {};
}

/* ───── Config fetch + edit (Free-tier in-Portal editor) ───── */

/**
 * Raw config blob shape — we don't pull the full MobiLauncherConfig
 * schema into the Portal because the shape changes more often than the
 * editor's UI surface. The editor treats this as opaque JSON for the
 * fields it doesn't edit; only the fields it edits are validated
 * client-side. Server-side Zod validation is the source of truth.
 */
export type ConfigBlob = Record<string, unknown>;

export type FetchConfigResult =
  | { kind: 'ok'; config: ConfigBlob }
  | { kind: 'not-found' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function fetchConfig(tenantId: string): Promise<FetchConfigResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/configs/${encodeURIComponent(tenantId)}`, {
      cache: 'no-store',
    });
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'network error',
    };
  }
  if (res.status === 404) return { kind: 'not-found' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  try {
    const config = (await res.json()) as ConfigBlob;
    return { kind: 'ok', config };
  } catch {
    return { kind: 'unexpected', status: res.status };
  }
}

export type SaveConfigResult =
  | { kind: 'ok'; etag: string; updatedAt: number }
  | { kind: 'unauthorized' }
  | { kind: 'invalid'; issues: Array<{ path: (string | number)[]; message: string }> }
  | { kind: 'tenantMismatch'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

export async function saveTenantConfig(input: {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (writes via the session cookie). */
  editToken: string | null;
  config: ConfigBlob;
}): Promise<SaveConfigResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/configs/${encodeURIComponent(input.tenantId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...bearerHeader(input.editToken),
        },
        body: JSON.stringify(input.config),
      },
    );
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'network error',
    };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 422) {
    const body = (await safeJson(res)) as {
      issues?: Array<{ path: (string | number)[]; message: string }>;
    };
    return { kind: 'invalid', issues: body.issues ?? [] };
  }
  if (res.status === 400) {
    const body = (await safeJson(res)) as { details?: string; error?: string };
    return {
      kind: 'tenantMismatch',
      message: body.details ?? body.error ?? 'bad request',
    };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { etag?: string; updatedAt?: number };
    if (typeof body.etag === 'string' && typeof body.updatedAt === 'number') {
      return { kind: 'ok', etag: body.etag, updatedAt: body.updatedAt };
    }
    return { kind: 'unexpected', status: 200, message: 'malformed success body' };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

/* ───── License lookup by tenant ───── */

/**
 * Same FetchResult shape as `fetchLicense`, but keyed off the tenant
 * (used post-Checkout to display the newly issued license without the
 * user needing to track a license ID).
 */
export async function fetchLicenseByTenant(tenantId: string): Promise<FetchResult> {
  const id = tenantId.trim();
  if (id.length === 0) return { kind: 'malformed', message: 'tenantId required' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/licenses/by-tenant/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : String(err) };
  }

  if (res.status === 404) return { kind: 'not-found' };
  if (res.status === 410) return { kind: 'revoked' };
  if (!res.ok) return { kind: 'network', message: `HTTP ${String(res.status)}` };

  const rawBlob = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBlob);
  } catch (err) {
    return {
      kind: 'malformed',
      message: err instanceof Error ? err.message : 'Could not parse JSON',
    };
  }
  const result = PortalLicense.safeParse(parsed);
  if (!result.success) {
    return { kind: 'malformed', message: 'Response did not match License shape' };
  }
  return { kind: 'ok', license: result.data, rawBlob };
}

/* ───── Stripe Checkout + Billing Portal ───── */

export type Plan = 'pro_monthly' | 'pro_yearly' | 'ent_monthly' | 'ent_yearly';

export type CheckoutSessionResult =
  | { kind: 'ok'; url: string }
  | { kind: 'unauthorized' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'stripe'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

export async function createCheckoutSession(input: {
  tenantId: string;
  editToken: string | null;
  plan: Plan;
  successUrl: string;
  cancelUrl: string;
}): Promise<CheckoutSessionResult> {
  const { tenantId, editToken, ...payload } = input;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/tenant/checkout/${encodeURIComponent(tenantId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...bearerHeader(editToken),
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid checkout request' };
  }
  if (res.status === 502) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'stripe', message: body.error ?? 'stripe upstream error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { url?: string };
    if (typeof body.url === 'string' && body.url.length > 0) {
      return { kind: 'ok', url: body.url };
    }
    return { kind: 'unexpected', status: 200, message: 'malformed success body' };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

export type BillingPortalResult =
  | { kind: 'ok'; url: string }
  | { kind: 'unauthorized' }
  | { kind: 'no-subscription' }
  | { kind: 'network'; message: string }
  | { kind: 'stripe'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

export async function openBillingPortal(input: {
  tenantId: string;
  editToken: string | null;
  returnUrl: string;
}): Promise<BillingPortalResult> {
  const { tenantId, editToken, ...payload } = input;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/tenant/billing-portal/${encodeURIComponent(tenantId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...bearerHeader(editToken),
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 400) return { kind: 'no-subscription' };
  if (res.status === 502) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'stripe', message: body.error ?? 'stripe upstream error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { url?: string };
    if (typeof body.url === 'string' && body.url.length > 0) {
      return { kind: 'ok', url: body.url };
    }
    return { kind: 'unexpected', status: 200, message: 'malformed success body' };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

/* ───── Magic-link recovery ───── */

export type RequestMagicLinkResult =
  | { kind: 'ok' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'send-failed'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Ask the server to email a one-time sign-in link. The server returns
 * 200 OK regardless of whether the email matches a real account — it
 * just doesn't send mail to unknown addresses. We surface that as
 * generic "if it matched, check your inbox" UX.
 */
export async function requestMagicLink(email: string): Promise<RequestMagicLinkResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/magic-link/request`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid request' };
  }
  if (res.status === 502) {
    const body = (await safeJson(res)) as { message?: string };
    return { kind: 'send-failed', message: body.message ?? 'email send failed' };
  }
  return { kind: 'unexpected', status: res.status };
}

export type RedeemMagicLinkResult =
  | { kind: 'ok' }
  | { kind: 'expired-or-used' }
  | { kind: 'not-found' }
  | { kind: 'invalid' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Redeem a one-time sign-in token (from `?signin=<token>` in the URL).
 * On success the server sets an HttpOnly session cookie; the caller
 * follows up with `fetchMe()` to read the signed-in user + their
 * fleets. `credentials: 'include'` is mandatory — without it the
 * browser won't accept the Set-Cookie response.
 */
export async function redeemMagicLink(token: string): Promise<RedeemMagicLinkResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/magic-link/redeem`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  if (res.status === 400) return { kind: 'invalid' };
  if (res.status === 404) return { kind: 'not-found' };
  if (res.status === 410) return { kind: 'expired-or-used' };
  return { kind: 'unexpected', status: res.status };
}

/* ───── Signed-in customer view (/me + /sign-out) ───── */

/**
 * Per-tenant entry returned by GET /me. Mirrors the server's MeTenant
 * shape and is what FleetsList renders. `editToken` is the bearer
 * credential used for PUT /tenant/configs/:tenantId etc. — it lives in
 * memory only (React state) and is NEVER written to localStorage.
 */
export type TeamRole = 'owner' | 'admin' | 'viewer';

export interface MeTenant {
  tenantId: string;
  deploymentName: string;
  tier: 'core' | 'pro' | 'enterprise';
  /** Bearer for writes — present ONLY for owners (server hands it out
   *  owner-only). Non-owner members write via the session cookie instead. */
  editToken: string | null;
  /** The signed-in customer's role on this tenant. */
  role: TeamRole;
  launcherUrl: string;
}

export type FetchMeResult =
  | { kind: 'ok'; email: string; tenants: MeTenant[]; totpEnabled: boolean }
  | { kind: 'unauthenticated' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Read the currently signed-in customer. Returns 'unauthenticated' on
 * 401 (no session cookie, expired, etc.) — the Portal renders the
 * sign-in form in that case.
 */
export async function fetchMe(): Promise<FetchMeResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/me`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status !== 200) return { kind: 'unexpected', status: res.status };

  const body = (await safeJson(res)) as {
    email?: string;
    tenants?: unknown;
    totpEnabled?: unknown;
  };
  if (typeof body.email !== 'string' || !Array.isArray(body.tenants)) {
    return { kind: 'unexpected', status: 200 };
  }
  const tenants: MeTenant[] = [];
  for (const raw of body.tenants) {
    if (
      typeof raw === 'object' &&
      raw !== null &&
      'tenantId' in raw &&
      'deploymentName' in raw &&
      'tier' in raw &&
      'launcherUrl' in raw
    ) {
      const t = raw as {
        tenantId: unknown;
        deploymentName: unknown;
        tier: unknown;
        editToken: unknown;
        role: unknown;
        launcherUrl: unknown;
      };
      if (
        typeof t.tenantId === 'string' &&
        typeof t.deploymentName === 'string' &&
        typeof t.launcherUrl === 'string' &&
        (t.tier === 'core' || t.tier === 'pro' || t.tier === 'enterprise') &&
        (t.editToken === null || typeof t.editToken === 'string')
      ) {
        tenants.push({
          tenantId: t.tenantId,
          deploymentName: t.deploymentName,
          tier: t.tier,
          editToken: t.editToken,
          // Default to 'owner' for back-compat with a server that predates
          // roles (shouldn't happen post-deploy, but keeps the SPA safe).
          role: t.role === 'admin' || t.role === 'viewer' ? t.role : 'owner',
          launcherUrl: t.launcherUrl,
        });
      }
    }
  }
  return { kind: 'ok', email: body.email, tenants, totpEnabled: body.totpEnabled === true };
}

/* ───── Team members ───── */

export interface TeamMember {
  customerId: string;
  email: string | null;
  role: string;
  isBillingOwner: boolean;
  createdAt: number;
}
export interface TeamInvite {
  token: string;
  email: string;
  role: string;
  expiresAt: number;
  createdAt: number;
}
export type MembersResult =
  | { kind: 'ok'; members: TeamMember[]; invites: TeamInvite[] }
  | { kind: 'forbidden' }
  | { kind: 'unauthenticated' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Simple mutation outcome for the team endpoints. */
export type TeamActionResult =
  | { kind: 'ok'; body: unknown }
  | { kind: 'forbidden' }
  | { kind: 'conflict'; message: string }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

async function teamAction(
  path: string,
  init: { method: 'POST' | 'PUT' | 'DELETE'; body?: unknown },
): Promise<TeamActionResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: init.method,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(init.body !== undefined && { 'content-type': 'application/json' }),
      },
      ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 409) {
    const b = (await safeJson(res)) as { error?: string };
    return { kind: 'conflict', message: b.error ?? 'conflict' };
  }
  if (res.status === 400) {
    const b = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: b.error ?? 'invalid' };
  }
  if (res.ok || res.status === 204) return { kind: 'ok', body: await safeJson(res) };
  return { kind: 'unexpected', status: res.status };
}

/** List a tenant's members (+ pending invites when the caller is an owner). */
export async function fetchMembers(tenantId: string): Promise<MembersResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/tenant/${encodeURIComponent(tenantId)}/members`, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status !== 200) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as { members?: TeamMember[]; invites?: TeamInvite[] };
  return {
    kind: 'ok',
    members: Array.isArray(body.members) ? body.members : [],
    invites: Array.isArray(body.invites) ? body.invites : [],
  };
}

export function inviteMember(tenantId: string, email: string, role: 'admin' | 'viewer') {
  return teamAction(`/tenant/${encodeURIComponent(tenantId)}/members/invite`, {
    method: 'POST',
    body: { email, role },
  });
}
export function updateMemberRole(tenantId: string, customerId: string, role: 'admin' | 'viewer') {
  return teamAction(`/tenant/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(customerId)}`, {
    method: 'PUT',
    body: { role },
  });
}
export function removeMember(tenantId: string, customerId: string) {
  return teamAction(`/tenant/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(customerId)}`, {
    method: 'DELETE',
  });
}
export function revokeInvite(tenantId: string, token: string) {
  return teamAction(`/tenant/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(token)}`, {
    method: 'DELETE',
  });
}

export type AcceptInviteResult =
  | { kind: 'ok'; tenantId: string; role: string }
  | { kind: 'invalid' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Accept a team invite by token — creates the account if new + logs in. */
export async function acceptInvite(token: string): Promise<AcceptInviteResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/members/accept`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 410 || res.status === 400) return { kind: 'invalid' };
  if (res.status !== 200) return { kind: 'unexpected', status: res.status };
  const b = (await safeJson(res)) as { tenantId?: string; role?: string };
  return { kind: 'ok', tenantId: b.tenantId ?? '', role: b.role ?? '' };
}

export type SignOutResult =
  | { kind: 'ok' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Sign out. Best-effort — even if the server's session row is already
 * gone, the response clears the cookie. The Portal then drops to the
 * sign-in screen.
 */
export async function signOut(): Promise<SignOutResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/sign-out`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 204) return { kind: 'ok' };
  return { kind: 'unexpected', status: res.status };
}

/* ───── Email + password auth ───── */

export type RequestPasswordEmailResult =
  | { kind: 'ok' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Ask the server to email a "set your password" (`purpose: 'verify'`) or
 * "reset your password" (`purpose: 'reset'`) link. Like the magic-link
 * request, the server always 200s for a well-formed email regardless of
 * whether an account exists — we surface generic "check your inbox" UX.
 */
export async function requestPasswordEmail(
  email: string,
  purpose: 'verify' | 'reset',
): Promise<RequestPasswordEmailResult> {
  const path = purpose === 'reset' ? '/auth/request-reset' : '/auth/request-verify';
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid request' };
  }
  return { kind: 'unexpected', status: res.status };
}

export type SetPasswordResult =
  | { kind: 'ok'; mfaRequired: boolean }
  | { kind: 'too-short'; message: string }
  | { kind: 'expired-or-used' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Redeem a set/reset token (from `?setpw=<token>`) and a chosen password.
 * On success the server sets the session cookie — `credentials: 'include'`
 * is mandatory. The caller follows up with `fetchMe()`.
 */
export async function setPassword(input: {
  token: string;
  password: string;
}): Promise<SetPasswordResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/set-password`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { mfaRequired?: boolean };
    return { kind: 'ok', mfaRequired: body.mfaRequired === true };
  }
  if (res.status === 400) {
    const body = (await safeJson(res)) as { message?: string };
    return { kind: 'too-short', message: body.message ?? 'Password is too short.' };
  }
  if (res.status === 410) return { kind: 'expired-or-used' };
  return { kind: 'unexpected', status: res.status };
}

export type PasswordSignInResult =
  | { kind: 'ok'; mfaRequired: boolean }
  | { kind: 'invalid' }
  | { kind: 'locked'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Sign in with email + password. On success the server sets the session
 * cookie (`credentials: 'include'` required); the caller then `fetchMe()`s.
 * A 401 is the deliberately-generic "invalid email or password"; a 429 means
 * the account is temporarily locked after too many failed attempts.
 */
export async function passwordSignIn(input: {
  email: string;
  password: string;
}): Promise<PasswordSignInResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/sign-in`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { mfaRequired?: boolean };
    return { kind: 'ok', mfaRequired: body.mfaRequired === true };
  }
  if (res.status === 401) return { kind: 'invalid' };
  if (res.status === 429) {
    const body = (await safeJson(res)) as { message?: string };
    return { kind: 'locked', message: body.message ?? 'Too many attempts — try again later.' };
  }
  return { kind: 'unexpected', status: res.status };
}

export type EmailLookupResult =
  | { kind: 'ok'; exists: boolean; hasPassword: boolean }
  | { kind: 'rate-limited' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Email-first entry check: does an account exist for this email? Drives the
 * signed-out Portal's single entry — known email → sign in, unknown → start
 * free. Rate-limited server-side.
 */
export async function lookupEmail(email: string): Promise<EmailLookupResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/lookup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { exists?: boolean; hasPassword?: boolean };
    return { kind: 'ok', exists: body.exists === true, hasPassword: body.hasPassword === true };
  }
  if (res.status === 429) return { kind: 'rate-limited' };
  return { kind: 'unexpected', status: res.status };
}

/* ───── TOTP two-factor ───── */

export type TotpEnrollResult =
  | { kind: 'ok'; secret: string; otpauthUri: string }
  | { kind: 'unauthenticated' }
  | { kind: 'not-configured' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Begin enrollment — returns the secret + otpauth URI to render as a QR. */
export async function totpEnroll(): Promise<TotpEnrollResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/totp/enroll`, { method: 'POST', credentials: 'include' });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { secret?: string; otpauthUri?: string };
    if (typeof body.secret === 'string' && typeof body.otpauthUri === 'string') {
      return { kind: 'ok', secret: body.secret, otpauthUri: body.otpauthUri };
    }
    return { kind: 'unexpected', status: 200 };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 500) return { kind: 'not-configured' };
  return { kind: 'unexpected', status: res.status };
}

export type TotpEnableResult =
  | { kind: 'ok'; recoveryCodes: string[] }
  | { kind: 'bad-code' }
  | { kind: 'unauthenticated' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Confirm a code to activate TOTP; returns one-time recovery codes. */
export async function totpEnable(input: {
  secret: string;
  code: string;
}): Promise<TotpEnableResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/totp/enable`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { recoveryCodes?: unknown };
    const codes = Array.isArray(body.recoveryCodes)
      ? body.recoveryCodes.filter((x): x is string => typeof x === 'string')
      : [];
    return { kind: 'ok', recoveryCodes: codes };
  }
  if (res.status === 400) return { kind: 'bad-code' };
  if (res.status === 401) return { kind: 'unauthenticated' };
  return { kind: 'unexpected', status: res.status };
}

export type TotpVerifyResult =
  | { kind: 'ok' }
  | { kind: 'bad-code' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Satisfy the 2FA challenge for an mfa_pending session (TOTP or recovery). */
export async function totpVerify(code: string): Promise<TotpVerifyResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/totp/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  if (res.status === 401) return { kind: 'bad-code' };
  return { kind: 'unexpected', status: res.status };
}

export type TotpDisableResult =
  | { kind: 'ok' }
  | { kind: 'bad-code' }
  | { kind: 'unauthenticated' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Turn off 2FA — requires a fresh TOTP or recovery code. */
export async function totpDisable(code: string): Promise<TotpDisableResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/totp/disable`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  // 401 covers both "wrong code" and "not signed in" — the server returns the
  // same status for both; from the disable UI they're equivalent (re-enter).
  if (res.status === 401) return { kind: 'bad-code' };
  return { kind: 'unexpected', status: res.status };
}

/* ───── Broadcasts (customer-managed) ───── */

export interface PortalBroadcast {
  id: string;
  tenantId: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  startsAt: number;
  expiresAt: number;
  dismissible: boolean;
  targetsJson: string | null;
  createdAt: number;
}

export type ListBroadcastsResult =
  | { kind: 'ok'; broadcasts: PortalBroadcast[] }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function listBroadcasts(input: {
  tenantId: string;
  editToken: string | null;
}): Promise<ListBroadcastsResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/broadcasts/${encodeURIComponent(input.tenantId)}`,
      {
        credentials: 'include',
        headers: { ...bearerHeader(input.editToken), accept: 'application/json' },
        cache: 'no-store',
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as { broadcasts?: PortalBroadcast[] };
  return { kind: 'ok', broadcasts: Array.isArray(body.broadcasts) ? body.broadcasts : [] };
}

/* ───────── Clone tenant (duplicate-as-template) ───────── */

export type CloneTenantResult =
  | { kind: 'ok'; tenantId: string; launcherUrl: string; editToken: string; deploymentName: string }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Duplicate an existing tenant's config into a new tenant slot owned
 * by the same customer. Returns a fresh tenantId + editToken on
 * success — the caller typically navigates straight to the new
 * tenant's editor.
 */
export async function cloneTenant(input: {
  sourceTenantId: string;
  editToken: string | null;
  deploymentName?: string;
}): Promise<CloneTenantResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/clone/${encodeURIComponent(input.sourceTenantId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...bearerHeader(input.editToken),
        },
        body: JSON.stringify(
          input.deploymentName !== undefined
            ? { deploymentName: input.deploymentName }
            : {},
        ),
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid request' };
  }
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as {
    tenantId?: string;
    launcherUrl?: string;
    editToken?: string;
    deploymentName?: string;
  };
  if (
    typeof body.tenantId !== 'string' ||
    typeof body.launcherUrl !== 'string' ||
    typeof body.editToken !== 'string' ||
    typeof body.deploymentName !== 'string'
  ) {
    return { kind: 'unexpected', status: res.status };
  }
  return {
    kind: 'ok',
    tenantId: body.tenantId,
    launcherUrl: body.launcherUrl,
    editToken: body.editToken,
    deploymentName: body.deploymentName,
  };
}

/* ───────── Delete fleet ───────── */

export type DeleteFleetResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Permanently delete a fleet (tenant config + its licenses, devices,
 * broadcasts, telemetry). Irreversible — callers must confirm first. Auth is
 * the tenant's edit token; the server 401s a wrong/missing token (and an
 * unknown tenant, without leaking existence).
 */
export async function deleteFleet(input: {
  tenantId: string;
  editToken: string | null;
}): Promise<DeleteFleetResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/tenant/configs/${encodeURIComponent(input.tenantId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...bearerHeader(input.editToken) },
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok' };
}

/* ───────── Telemetry (Enterprise) ───────── */

export interface PortalTelemetryEvent {
  id: number;
  category: string;
  name: string;
  /** Device-side ms epoch from the emitter. */
  ts: number;
  /** Server-side ms epoch at ingest. Source of truth for ordering. */
  receivedAt: number;
  /** Already JSON-parsed by the server; undefined when the event had no attrs. */
  attrs?: unknown;
}

export type ListTelemetryEventsResult =
  | { kind: 'ok'; events: PortalTelemetryEvent[] }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Customer view: GET /tenant/telemetry/:tenantId/recent — recent events
 * the customer's fleet has flushed. Bearer-token auth. Used by the
 * Portal's Telemetry tab to give a quick "is the pipeline working"
 * surface without standing up Datadog/Splunk first.
 */
export async function listTelemetryEvents(input: {
  tenantId: string;
  editToken: string | null;
  limit?: number;
  category?: string;
}): Promise<ListTelemetryEventsResult> {
  const url = new URL(
    `${API_BASE}/tenant/telemetry/${encodeURIComponent(input.tenantId)}/recent`,
  );
  if (input.limit !== undefined) url.searchParams.set('limit', String(input.limit));
  if (input.category !== undefined && input.category.length > 0) {
    url.searchParams.set('category', input.category);
  }
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      credentials: 'include',
      headers: {
        ...bearerHeader(input.editToken),
        accept: 'application/json',
      },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as { events?: PortalTelemetryEvent[] };
  return { kind: 'ok', events: Array.isArray(body.events) ? body.events : [] };
}

export type CreateBroadcastResult =
  | { kind: 'ok'; broadcast: PortalBroadcast }
  | { kind: 'unauthorized' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

export async function createBroadcast(input: {
  tenantId: string;
  editToken: string | null;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  startsAt?: number;
  expiresAt: number;
  dismissible: boolean;
}): Promise<CreateBroadcastResult> {
  const { tenantId, editToken, ...payload } = input;
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/broadcasts/${encodeURIComponent(tenantId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...bearerHeader(editToken),
        },
        body: JSON.stringify(payload),
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as {
      error?: string;
      issues?: Array<{ message?: string }>;
    };
    return {
      kind: 'invalid',
      message: body.issues?.[0]?.message ?? body.error ?? 'invalid broadcast',
    };
  }
  if (res.status === 201) {
    const body = (await safeJson(res)) as PortalBroadcast;
    return { kind: 'ok', broadcast: body };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

export type DeleteBroadcastResult =
  | { kind: 'ok' }
  | { kind: 'not-found' }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function deleteBroadcast(input: {
  tenantId: string;
  editToken: string | null;
  id: string;
}): Promise<DeleteBroadcastResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/broadcasts/${encodeURIComponent(input.tenantId)}/${encodeURIComponent(input.id)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...bearerHeader(input.editToken) },
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 204) return { kind: 'ok' };
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  return { kind: 'unexpected', status: res.status };
}

/* ───── Devices ───── */

export interface PortalDevice {
  id: string;
  tenantId: string;
  androidId: string | null;
  deviceName: string | null;
  osVersion: string | null;
  securityPatch: string | null;
  appVersion: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  localIpv4: string | null;
  city: string | null;
  country: string | null;
  bootCount: number;
}

/* ───── Admin dashboard (Bearer = ADMIN_BEARER_TOKEN) ───── */

export interface AdminTenantRow {
  tenantId: string;
  ownerEmail: string | null;
  tier: 'core' | 'pro' | 'enterprise';
  devices: number;
  activeDevices: number;
  updatedAt: number;
}
export interface AdminCustomerRow {
  id: string;
  email: string;
  stripeLinked: boolean;
  tenantCount: number;
  createdAt: number;
}
export interface AdminOverview {
  counts: { customers: number; tenants: number; devices: number; activeDevices: number };
  /** Change vs the prior daily snapshot; null until there's a baseline. */
  deltas: { customers: number; tenants: number; devices: number } | null;
  /**
   * Real MRR from captured Stripe prices (monthly-normalized cents). Null when
   * billing isn't wired. `pricedSubs`/`totalPaidSubs` expose coverage — subs
   * awaiting their next webhook count toward the denominator only.
   */
  mrr: { monthlyCents: number; pricedSubs: number; totalPaidSubs: number } | null;
  tierMix: { core: number; pro: number; enterprise: number };
  tenants: AdminTenantRow[];
  customers: AdminCustomerRow[];
}
export type AdminOverviewResult =
  | { kind: 'ok'; overview: AdminOverview }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export type AdminWhoamiResult =
  | { kind: 'staff'; via: 'session' | 'token'; email: string | null }
  | { kind: 'unauthenticated' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * Probe whether the current caller may use the admin console. The console calls
 * this on load with just the session cookie (no token) — a 200 means a staff
 * account is signed in and the console renders without asking for a token. Pass
 * a break-glass token to authorize that way instead.
 */
export async function fetchAdminWhoami(token = ''): Promise<AdminWhoamiResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/whoami`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status !== 200) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as { via?: unknown; email?: unknown };
  return {
    kind: 'staff',
    via: body.via === 'token' ? 'token' : 'session',
    email: typeof body.email === 'string' ? body.email : null,
  };
}

/** Cross-tenant admin dashboard payload. `token` is empty for a staff session,
 *  or the break-glass ADMIN_BEARER_TOKEN. */
export async function fetchAdminOverview(token: string): Promise<AdminOverviewResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/overview`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', overview: (await safeJson(res)) as AdminOverview };
}

export interface AdminTenantDetail {
  tenant: {
    tenantId: string;
    deploymentName: string | null;
    ownerEmail: string | null;
    ownerCustomerId: string | null;
    tier: 'core' | 'pro' | 'enterprise';
    updatedAt: number;
  };
  licenses: {
    id: string;
    tier: 'core' | 'pro' | 'enterprise';
    status: 'active' | 'revoked';
    expiresAt: number | null;
    createdAt: number;
    stripeLinked: boolean;
  }[];
  devices: {
    id: string;
    deviceName: string | null;
    osVersion: string | null;
    localIpv4: string | null;
    bootCount: number;
    lastSeenAt: number;
  }[];
  broadcasts: {
    id: string;
    title: string;
    severity: 'info' | 'warning' | 'critical';
    startsAt: number;
    expiresAt: number;
  }[];
  /** Recent privileged events (admin exit gesture, PIN attempts, overlay). */
  audit: {
    id: number;
    name: string;
    /** Stringified JSON attrs, or null. */
    attrs: string | null;
    ts: number;
    receivedAt: number;
  }[];
}
export type AdminTenantDetailResult =
  | { kind: 'ok'; detail: AdminTenantDetail }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Drill-down for one tenant: license history, devices, broadcasts. */
export async function fetchTenantDetail(
  token: string,
  tenantId: string,
): Promise<AdminTenantDetailResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', detail: (await safeJson(res)) as AdminTenantDetail };
}

/**
 * Funnel + real-vs-bot analytics, queried server-side from Analytics Engine.
 * `configured: false` means the Worker has no `CF_ACCOUNT_ID`/`CF_API_TOKEN`
 * set yet — the UI shows a setup hint instead of empty charts.
 */
export type AdminAnalytics =
  | { configured: false }
  | { configured: true; error: string }
  | {
      configured: true;
      days: number;
      funnel: { event: string; outcome: string; n: number }[];
      signups: { source: string; signups: number }[];
      blocked: { event: string; blocked: number }[];
    };
export type AdminAnalyticsResult =
  | { kind: 'ok'; analytics: AdminAnalytics }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Funnel + abuse analytics for the admin dashboard. `days` is clamped 1–90. */
export async function fetchAdminAnalytics(
  token: string,
  days: number,
): Promise<AdminAnalyticsResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/analytics?days=${String(days)}`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  // A 502 still carries a JSON body ({ configured:true, error }); surface it.
  if (!res.ok && res.status !== 502) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', analytics: (await safeJson(res)) as AdminAnalytics };
}

/** Cross-tenant device health. Timestamps are unix seconds. */
export interface AdminFleet {
  now: number;
  activeWindowSec: number;
  staleWindowSec: number;
  capped: boolean;
  counts: { total: number; online: number; stale: number };
  patchSpread: { label: string; count: number }[];
  osSpread: { label: string; count: number }[];
  devices: {
    id: string;
    tenantId: string;
    deviceName: string | null;
    osVersion: string | null;
    securityPatch: string | null;
    appVersion: string | null;
    localIpv4: string | null;
    country: string | null;
    bootCount: number;
    lastSeenAt: number;
  }[];
}
export type AdminFleetResult =
  | { kind: 'ok'; fleet: AdminFleet }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Cross-tenant device fleet + health summary. */
export async function fetchAdminFleet(token: string, limit = 1000): Promise<AdminFleetResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/fleet?limit=${String(limit)}`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', fleet: (await safeJson(res)) as AdminFleet };
}

/** Full device record for the fleet drill-down. Timestamps are unix seconds. */
export interface AdminDeviceDetail {
  id: string;
  tenantId: string;
  androidId: string | null;
  deviceName: string | null;
  osVersion: string | null;
  securityPatch: string | null;
  appVersion: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  localIpv4: string | null;
  city: string | null;
  country: string | null;
  bootCount: number;
}
/** One device health heartbeat snapshot (drill-down). */
export interface DeviceHealth {
  reportedAt: number;
  receivedAt: number;
  tier: string | null;
  online: boolean | null;
  batteryPct: number | null;
  batteryCharging: boolean | null;
  signalDbm: number | null;
  storageUsedBytes: number | null;
  storageTotalBytes: number | null;
  memoryUsedMb: number | null;
  memoryTotalMb: number | null;
  uptimeSec: number | null;
  appVersion: string | null;
  configVersion: string | null;
  licenseStatus: string | null;
  licenseId: string | null;
}
export type AdminDeviceResult =
  | { kind: 'ok'; device: AdminDeviceDetail; latest: DeviceHealth | null; history: DeviceHealth[] }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/**
 * One device's full record (admin fleet drill-down) plus its latest health and
 * recent health history (newest first) for trend charts.
 */
export async function fetchAdminDevice(token: string, id: string): Promise<AdminDeviceResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/devices/${encodeURIComponent(id)}`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as {
    device: AdminDeviceDetail;
    latest?: DeviceHealth | null;
    health?: DeviceHealth[];
  };
  return {
    kind: 'ok',
    device: body.device,
    latest: body.latest ?? null,
    history: Array.isArray(body.health) ? body.health : [],
  };
}

/**
 * Public system-status snapshot (the 5-min uptime monitor behind
 * status.mobilauncher.com). No auth — surfaced on the admin Overview so
 * endpoint health lives in the same console.
 */
export type SystemStatusLevel = 'operational' | 'degraded' | 'partial-outage' | 'major-outage';
export interface SystemStatusComponent {
  id: string;
  status: SystemStatusLevel;
  latencyMs: number;
  checkedAt: string;
  httpStatus: number;
  error?: string;
}
export interface SystemStatus {
  updatedAt: string;
  results: SystemStatusComponent[];
}
export type SystemStatusResult =
  | { kind: 'ok'; status: SystemStatus }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function fetchStatus(): Promise<SystemStatusResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/status`, { headers: { accept: 'application/json' }, cache: 'no-store' });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', status: (await safeJson(res)) as SystemStatus };
}

/* ───── Admin operator actions (write side) ───── */

export type AdminActionResult =
  | { kind: 'ok'; body: unknown }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

/** Shared POST/DELETE caller for admin actions; maps statuses to a result union. */
async function adminAction(
  token: string,
  path: string,
  init: { method: 'POST' | 'DELETE'; body?: unknown },
): Promise<AdminActionResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: init.method,
      credentials: 'include',
      headers: {
        ...bearerHeader(token),
        accept: 'application/json',
        ...(init.body !== undefined && { 'content-type': 'application/json' }),
      },
      ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  // 409 (dup email/tenant) reads the same as a 400 to the UI — a message to
  // show, not a thrown error.
  if (res.status === 400 || res.status === 409 || res.status === 422) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid request' };
  }
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', body: await safeJson(res) };
}

/** Revoke a license by id. Idempotent server-side. */
export function adminRevokeLicense(token: string, licenseId: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/licenses/${encodeURIComponent(licenseId)}/revoke`, {
    method: 'POST',
  });
}

/** Backfill telemetry + remoteConfig defaults on a tenant's stored config. */
export function adminReseedDefaults(token: string, tenantId: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/reseed-tenant-defaults/${encodeURIComponent(tenantId)}`, {
    method: 'POST',
  });
}

export interface AdminBroadcastInput {
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  /** unix seconds. */
  expiresAt: number;
  dismissible: boolean;
}

/** Post a broadcast to a tenant's fleet. */
export function adminCreateBroadcast(
  token: string,
  tenantId: string,
  input: AdminBroadcastInput,
): Promise<AdminActionResult> {
  return adminAction(token, `/admin/broadcasts/${encodeURIComponent(tenantId)}`, {
    method: 'POST',
    body: input,
  });
}

/** Delete a single broadcast by id. */
export function adminDeleteBroadcast(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/broadcasts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Populate the billing table from Stripe so MRR is accurate immediately. */
export function adminBackfillBilling(token: string): Promise<AdminActionResult> {
  return adminAction(token, '/admin/billing/backfill', { method: 'POST' });
}

/** Set or clear a device's nickname (empty string clears it). */
export function adminRenameDevice(
  token: string,
  id: string,
  nickname: string,
): Promise<AdminActionResult> {
  return adminAction(token, `/admin/devices/${encodeURIComponent(id)}/nickname`, {
    method: 'POST',
    body: { nickname },
  });
}

/** Hard-delete a device row (it re-appears if the unit boots again). */
export function adminDeleteDevice(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Add or remove a single tag in a device's metadata.tags set. */
export function adminSetDeviceTag(
  token: string,
  id: string,
  tag: string,
  op: 'add' | 'remove',
): Promise<AdminActionResult> {
  return adminAction(token, `/admin/devices/${encodeURIComponent(id)}/tags`, {
    method: 'POST',
    body: { tag, op },
  });
}

/**
 * Issue a fresh signed license for a tenant at the given tier (the renderer
 * picks the newest active one on its next refresh). `expiresAt` is unix
 * seconds; omit for perpetual. Server fills tier-default features.
 */
export function adminIssueLicense(
  token: string,
  input: { tenantId: string; tier: 'core' | 'pro' | 'enterprise'; expiresAt?: number },
): Promise<AdminActionResult> {
  const body: Record<string, unknown> = { tenantId: input.tenantId, tier: input.tier };
  if (input.expiresAt !== undefined) body['expiresAt'] = input.expiresAt;
  return adminAction(token, '/admin/licenses/by-tenant', { method: 'POST', body });
}

/* ───── Admin CRUD: customers ───── */

/** Editable customer fields + owned tenants, for the admin edit panel. */
export interface AdminCustomerDetail {
  id: string;
  email: string;
  createdAt: number;
  emailVerifiedAt: number | null;
  lockoutUntil: number | null;
  failedLoginCount: number;
  totpEnabled: boolean;
  stripeCustomerId: string | null;
  tenants: { tenantId: string; deploymentName: string | null; launcherUrl: string }[];
}
export type AdminCustomerResult =
  | { kind: 'ok'; customer: AdminCustomerDetail }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function fetchAdminCustomer(token: string, id: string): Promise<AdminCustomerResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/admin/customers/${encodeURIComponent(id)}`, {
      credentials: 'include',
      headers: { ...bearerHeader(token), accept: 'application/json' },
      cache: 'no-store',
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  return { kind: 'ok', customer: (await safeJson(res)) as AdminCustomerDetail };
}

/** Create a Free customer by email (409 → invalid on a dup). */
export function adminCreateCustomer(token: string, email: string): Promise<AdminActionResult> {
  return adminAction(token, '/admin/customers', { method: 'POST', body: { email } });
}

/** Change a customer's email (409 → invalid on a clash). */
export function adminUpdateCustomerEmail(token: string, id: string, email: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}/email`, { method: 'POST', body: { email } });
}

/** Clear a customer's lockout + failed-login counter. */
export function adminUnlockCustomer(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}/unlock`, { method: 'POST' });
}

/** Disable a customer's TOTP so they can re-enrol. */
export function adminResetCustomer2fa(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}/reset-2fa`, { method: 'POST' });
}

/** Force email_verified on a customer. */
export function adminVerifyCustomerEmail(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}/verify-email`, { method: 'POST' });
}

/** Admin-set a customer's password (hashed server-side; also marks verified + unlocks). */
export function adminSetCustomerPassword(token: string, id: string, password: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}/password`, { method: 'POST', body: { password } });
}

/** Hard-delete a customer (cascades their tenants + devices + sessions). */
export function adminDeleteCustomer(token: string, id: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/customers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ───── Admin CRUD: tenants ───── */

/** Create a Free tenant owned by a customer (seeds a starter launcher). */
export function adminCreateTenant(token: string, ownerCustomerId: string, name: string): Promise<AdminActionResult> {
  return adminAction(token, '/admin/tenants', { method: 'POST', body: { ownerCustomerId, name } });
}

/** Rename a tenant and/or reassign its owner. */
export function adminUpdateTenant(
  token: string,
  tenantId: string,
  input: { deploymentName?: string; ownerCustomerId?: string },
): Promise<AdminActionResult> {
  return adminAction(token, `/admin/tenants/${encodeURIComponent(tenantId)}/update`, { method: 'POST', body: input });
}

/** Hard-delete a tenant (cascades licenses + devices + broadcasts + telemetry). */
export function adminDeleteTenant(token: string, tenantId: string): Promise<AdminActionResult> {
  return adminAction(token, `/admin/tenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
}

export type ListDevicesResult =
  | { kind: 'ok'; devices: PortalDevice[] }
  | { kind: 'unauthorized' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function listDevices(input: {
  tenantId: string;
  editToken: string | null;
}): Promise<ListDevicesResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/devices/${encodeURIComponent(input.tenantId)}`,
      {
        credentials: 'include',
        headers: { ...bearerHeader(input.editToken), accept: 'application/json' },
        cache: 'no-store',
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) return { kind: 'unauthorized' };
  if (!res.ok) return { kind: 'unexpected', status: res.status };
  const body = (await safeJson(res)) as { devices?: PortalDevice[] };
  return { kind: 'ok', devices: Array.isArray(body.devices) ? body.devices : [] };
}

export type UpdateDeviceResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function updateDevice(input: {
  tenantId: string;
  editToken: string | null;
  deviceId: string;
  nickname?: string | null;
  tags?: string[];
}): Promise<UpdateDeviceResult> {
  // Send only the fields the caller is changing — the endpoint merges into the
  // device's metadata bag, so omitting one leaves it untouched.
  const body: Record<string, unknown> = {};
  if (input.nickname !== undefined) body['nickname'] = input.nickname;
  if (input.tags !== undefined) body['tags'] = input.tags;
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/devices/${encodeURIComponent(input.tenantId)}/${encodeURIComponent(input.deviceId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...bearerHeader(input.editToken),
        },
        body: JSON.stringify(body),
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 200) return { kind: 'ok' };
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string };
    return { kind: 'invalid', message: body.error ?? 'invalid update' };
  }
  return { kind: 'unexpected', status: res.status };
}

export type DeleteDeviceResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number };

export async function deleteDevice(input: {
  tenantId: string;
  editToken: string | null;
  deviceId: string;
}): Promise<DeleteDeviceResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/tenant/devices/${encodeURIComponent(input.tenantId)}/${encodeURIComponent(input.deviceId)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...bearerHeader(input.editToken) },
      },
    );
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 204) return { kind: 'ok' };
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'not-found' };
  return { kind: 'unexpected', status: res.status };
}
